import { useCallback, useEffect, useMemo, useState } from "react";

import { NovedadesExportAnalyticsHelper } from "@core/Analytics/NovedadesExportAnalyticsHelper";
import {
    NovedadesExportService,
    NovedadesExportServiceModel,
} from "@services/NovedadesExportService";
import { Description } from "@shared/utils/DescriptionsHelper";

export interface ExportNovedadesEmployeeListItem {
    id: string;
    name: string;
    legajo: string;
    workedHours: string;
    extraHours: string;
    statusTotals: Record<string, string>;
    raw: NovedadesExportServiceModel.ProcessEmployee;
}

export interface ExportNovedadesEmployeeTotalsRow {
    label: string;
    legajo: string;
    workedHours: string;
    extraHours: string;
    statusTotals: Record<string, string>;
}

export interface ExportNovedadesEmployeeStatusColumn {
    totalsField: string;
    label: string;
}

interface UseExportNovedadesEmployeesStepViewModelProps {
    processID: string;
    organizationID: string;
    service: NovedadesExportService;
}

const formatNumber = (value: number | undefined): string => {
    return Description.Number.decimal(value, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    });
};

const minutesToHours = (minutes: number | undefined): string => {
    return formatNumber((minutes ?? 0) / 60);
};

const employeeDisplayName = (
    employee: NovedadesExportServiceModel.ProcessEmployee,
): string => {
    return employee.name || employee.displayName || employee.employeeID || "N/D";
};

const mapEmployeeToListItem = (
    employee: NovedadesExportServiceModel.ProcessEmployee,
    statusColumns: ExportNovedadesEmployeeStatusColumn[],
): ExportNovedadesEmployeeListItem => {
    const totals = employee.totals ?? {};
    const extraMinutes = (totals.extra50Minutes ?? 0) + (totals.extra100Minutes ?? 0);

    return {
        id: employee.employeeID,
        name: employeeDisplayName(employee),
        legajo: employee.legajo || employee.payrollEmployeeID || "N/D",
        workedHours: minutesToHours(totals.totalMinutes),
        extraHours: minutesToHours(extraMinutes),
        statusTotals: statusColumns.reduce((result, column) => {
            result[column.totalsField] = formatNumber(totals[column.totalsField]);
            return result;
        }, {} as Record<string, string>),
        raw: employee,
    };
};

export const mapEmployeesToListItems = (
    employees: NovedadesExportServiceModel.ProcessEmployee[],
    statusColumns: ExportNovedadesEmployeeStatusColumn[],
): ExportNovedadesEmployeeListItem[] => {
    return employees.map((employee) => mapEmployeeToListItem(employee, statusColumns));
};

const normalizeSearchText = (value: string): string => {
    return value.toLowerCase().replace(/\s/g, "");
};

const buildSearchText = (row: ExportNovedadesEmployeeListItem): string => {
    return normalizeSearchText([
        row.name,
        row.legajo,
        row.raw.mobilePhone,
        row.raw.employeeID,
        row.raw.documentNumber,
        row.raw.payrollEmployeeID,
    ].filter(Boolean).join(" "));
};

const sumRows = (
    rows: ExportNovedadesEmployeeListItem[],
    selector: (totals: Record<string, number>) => number,
): number => {
    return rows.reduce((total, row) => total + selector(row.raw.totals ?? {}), 0);
};

export const buildEmployeeTotalsRow = (
    rows: ExportNovedadesEmployeeListItem[],
    statusColumns: ExportNovedadesEmployeeStatusColumn[],
): ExportNovedadesEmployeeTotalsRow => {
    const totalMinutes = sumRows(rows, (totals) => totals.totalMinutes ?? 0);
    const extraMinutes = sumRows(
        rows,
        (totals) => (totals.extra50Minutes ?? 0) + (totals.extra100Minutes ?? 0),
    );

    return {
        label: "Totales".localized(),
        legajo: "",
        workedHours: minutesToHours(totalMinutes),
        extraHours: minutesToHours(extraMinutes),
        statusTotals: statusColumns.reduce((result, column) => {
            result[column.totalsField] = formatNumber(sumRows(rows, (totals) => totals[column.totalsField] ?? 0));
            return result;
        }, {} as Record<string, string>),
    };
};

export const useExportNovedadesEmployeesStepViewModel = ({
    processID,
    organizationID,
    service,
}: UseExportNovedadesEmployeesStepViewModelProps) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [employees, setEmployees] = useState<NovedadesExportServiceModel.ProcessEmployee[]>([]);
    const [statusColumns, setStatusColumns] = useState<ExportNovedadesEmployeeStatusColumn[]>([]);
    const [searchField, setSearchField] = useState("");

    const refresh = useCallback(async () => {
        if (!processID) {
            setEmployees([]);
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const [nextEmployees, configuration] = await Promise.all([
                service.listEmployees(processID),
                service.getStatusMappingConfiguration(organizationID),
            ]);
            const nextStatusColumns = configuration.groups.map((group) => ({
                totalsField: group.totalsField,
                label: group.label,
            }));
            setEmployees(nextEmployees);
            setStatusColumns(nextStatusColumns);
            NovedadesExportAnalyticsHelper.trackEmployeesShown({
                processID,
                employeeCount: nextEmployees.length,
                statusColumnCount: nextStatusColumns.length,
                customStatusColumnCount: configuration.groups.filter((group) => group.builtIn !== true).length,
            });
        } catch (loadError) {
            console.error(loadError);
            setError("No se pudieron cargar los colaboradores de la exportación.".localized());
        } finally {
            setLoading(false);
        }
    }, [organizationID, processID, service]);

    useEffect(() => {
        void refresh();
    }, [refresh]);

    const allRows = useMemo(() => {
        return mapEmployeesToListItems(employees, statusColumns);
    }, [employees, statusColumns]);

    const rows = useMemo(() => {
        const normalizedSearch = normalizeSearchText(searchField);
        if (!normalizedSearch) {
            return allRows;
        }

        return allRows.filter((row) => buildSearchText(row).includes(normalizedSearch));
    }, [allRows, searchField]);

    const totalsRow = useMemo(() => {
        return buildEmployeeTotalsRow(rows, statusColumns);
    }, [rows, statusColumns]);

    return {
        loading,
        error,
        searchField,
        setSearchField,
        statusColumns,
        rows,
        totalsRow,
        refresh,
    };
};
